export const supportedLanguages = ['en', 'fr'];
export const defaultLanguage = 'en';

const sharedSkills = [
  'Flutter',
  'Android',
  'iOS',
  'Kotlin',
  'Python',
  'Swift',
  'KMP',
  'Node.js',
  'Ktor',
  'OpenAPI',
  'TypeScript',
  'Docker',
  'CI/CD',
  'Clean Architecture',
  'AI-Assisted Delivery',
];

export const localizedContent = {
  en: {
    head: {
      title: 'Hedi Hidouri | Senior Software Engineer',
      description:
        'Senior Software Engineer building mobile apps, backend services, and AI-enabled delivery workflows.',
      locale: 'en_US',
      keywords: [
        'Senior Software Engineer',
        'Software Engineer Paris',
        'Freelance Software Engineer Paris',
        'Flutter Developer',
        'Android Developer',
        'iOS Developer',
        'Backend Engineer',
        'Kotlin Multiplatform',
        'Ktor',
        'Node.js',
        'OpenAPI',
        'Python',
        'Docker',
        'Mobile Architecture',
        'CI/CD Mobile',
        'AI-Assisted Engineering',
      ],
    },
    layout: {
      skipToContent: 'Skip to Content',
      whatsAppCta: 'WhatsApp',
      whatsAppAriaLabel: 'Open WhatsApp chat',
    },
    nav: {
      homeAriaLabel: 'Home',
      menuButtonAriaLabel: 'Menu',
      languageSwitcherAriaLabel: 'Select language',
      links: [
        { name: 'About', url: '#about' },
        { name: 'Experience', url: '#jobs' },
        { name: 'Expertise', url: '#services' },
        { name: 'CV', url: '#cv' },
        { name: 'Contact', url: '#contact' },
      ],
    },
    hero: {
      intro: 'Hi, my name is',
      title: 'Hedi Hidouri.',
      subtitle: 'Senior Software Engineer.',
      description:
        'I design, build, and modernize software products across mobile apps, backend services, and AI-enabled delivery workflows. My focus is simple: reliable execution, durable architecture, and secure delivery from MVP to scale.',
      quickStats: [
        { label: 'Location', value: 'Paris, France' },
        { label: 'Experience', value: '8+ years' },
      ],
      cta: 'Let\'s Work Together',
    },
    about: {
      heading: 'About Me',
      paragraphs: [
        'I support tech, fintech, and product teams in designing, building, and modernizing reliable software products that are built to last.',
        'Across high-scale and high-constraint products (logistics, payments, mobility, banking), I help teams deliver robust mobile applications, streamline backend interactions, and structure maintainable mobile + backend architectures.',
      ],
      valueTitle: 'What I Bring',
      valueList: [
        'End-to-end ownership: mobile, backend, architecture, CI/CD.',
        'Strong expertise in security-sensitive systems and compliance constraints.',
        'Ability to frame products technically from MVP stage to large-scale platforms.',
        'Pragmatic delivery mindset focused on production outcomes.',
      ],
      techIntro: 'Core technologies I use regularly:',
      skills: sharedSkills,
      snapshotTitle: 'Profile Snapshot',
      snapshotStats: [
        { label: 'Role', value: 'Senior Software Engineer' },
        { label: 'Location', value: 'Paris, France (hybrid / remote)' },
        { label: 'Languages', value: 'French, English' },
        {
          label: 'Preferred Environments',
          value: 'Product teams, Agile delivery, high-impact software platforms',
        },
      ],
      domains: ['Fintech & Banking', 'E-commerce', 'Retail', 'Digital Platforms', 'Services IT'],
    },
    jobs: {
      heading: 'Professional Experience',
      tabsAriaLabel: 'Job tabs',
      items: [
        {
          title: 'Senior Mobile Engineer',
          company: 'INGroupe',
          location: 'Paris, France',
          range: 'Apr 2025 - Present',
          url: 'https://ingroupe.com/',
          summary:
            'Working across EUDI use cases end to end: Android, iOS, Flutter, React Native and KMP apps/SDKs, Ktor issuer and verifier backends, proximity verification flows, and Docker/Docker Compose environments. Scope includes wallet, issuer, verifier, age verification, digital badging, identity sharing and payment-related scenarios.',
          points: [
            'Working across the EUDI ecosystem end to end: wallet apps, issuer services, verifier components, proximity flows, and SDK integrations.',
            'Developing Android, iOS, Flutter, and Kotlin Multiplatform components for secure credential issuance, presentation, and verification use cases.',
            'Building Ktor backend services for issuer/verifier orchestration, API contracts, and payment-adjacent journeys, with Dockerized local environments.',
            'Designing SDKs and integration layers for proximity verification across native mobile and cross-platform clients, including QR, NFC/BLE, and mdoc-based exchanges.',
            'Creating Docker and Docker Compose stacks to run complete EUDI scenarios locally and accelerate testing, demos, and end-to-end delivery.',
            'Delivering use cases including age verification, digital badging, identity sharing, proximity verification, and payment-related flows with CI/CD quality gates.',
          ],
        },
        {
          title: 'Senior Mobile Engineer',
          company: 'Stuart',
          location: 'Paris, France',
          range: 'Nov 2022 - Apr 2025',
          url: 'https://stuart.com/',
          points: [
            'Led the modernization of Android and iOS user experiences with Flutter for a high-scale logistics platform.',
            'Developed and supervised a mobile Backend For Frontend in Node.js with endpoints tailored for app needs.',
            'Orchestrated backend components with Docker and Kafka to improve reliability between services and apps.',
            'Structured and scaled CI/CD pipelines with GitHub Actions and Fastlane for automated build and release flows.',
            'Raised software quality with unit and integration testing in an Agile delivery context.',
            'Contributed to core functional domains including dispatch optimization, delivery tracking, and payment evolution.',
          ],
        },
        {
          title: 'Mobile Development Consultant',
          company: 'Carrefour',
          location: 'Paris, France',
          range: 'May 2021 - Nov 2022',
          url: 'https://www.carrefour.com/',
          points: [
            'Contributed to large-scale evolution of Carrefour Drive and Delivery Android applications.',
            'Delivered new features and UI/UX refactors to improve fluidity, performance, and customer experience.',
            'Modernized architecture using MVI and Clean Architecture with reactive stacks (Flow, Coroutines).',
            'Introduced dependency injection and modularization practices with Hilt for better maintainability.',
            'Improved mobile CI through Jenkins and Gradle Kotlin DSL (KTS) automation.',
            'Reinforced reliability with unit and integration tests while collaborating closely with product and design teams.',
          ],
        },
        {
          title: 'Mobile Android / iOS Engineer',
          company: 'JCDecaux',
          location: 'Paris, France',
          range: 'Jan 2021 - May 2021',
          url: 'https://www.jcdecaux.com/',
          points: [
            'Developed and continuously improved bike-sharing mobile apps used by over one million users.',
            'Implemented new Android and iOS features while optimizing overall app performance.',
            'Reduced production incidents through targeted stability and bug-fix initiatives.',
            'Implemented unit and integration tests for Android and iOS, achieving strong coverage.',
            'Worked in close collaboration with product teams to clarify business needs and improve UX outcomes.',
            'Delivered in Agile/Scrum cycles with active planning and execution ownership.',
          ],
        },
        {
          title: 'iOS Mobile Engineer',
          company: 'HSBC',
          location: 'Paris, France',
          range: 'Mar 2019 - Dec 2020',
          url: 'https://www.hsbc.com/',
          points: [
            'Enhanced the iOS banking application with continuous UX and functional improvements.',
            'Implemented advanced security capabilities, including biometric authentication and wallet provisioning.',
            'Translated business and compliance requirements into scalable technical solutions.',
            'Delivered unit and integration testing and supported internal acceptance testing phases.',
            'Investigated and resolved production incidents to improve app stability and quality of service.',
            'Contributed to internal library enrichment, documentation, and engineering best-practice initiatives.',
          ],
        },
        {
          title: 'Mobile Software Engineer',
          company: 'Bulldozer',
          location: 'Ariana, Tunisia',
          range: 'Jul 2017 - Feb 2019',
          url: 'https://www.linkedin.com/company/bulldozer/',
          points: [
            'Contributed to the full mobile lifecycle: planning, development, testing, and release on Google Play and App Store.',
            'Produced technical studies and architecture designs for multi-domain client products.',
            'Developed Android and iOS features with strong focus on performance, code quality, and maintainability.',
            'Implemented unit, integration, and UI test strategies to improve release confidence.',
            'Participated in technical migrations and modernization of existing mobile codebases.',
            'Worked in Agile (Scrum/Kanban) environments while contributing to team rituals and continuous improvement.',
          ],
        },
      ],
    },
    services: {
      heading: 'Expertise & Services',
      detailsLabel: 'Details',
      items: [
        {
          title: 'Software Product Delivery',
          description:
            'I build production-grade software products across mobile apps and supporting services, from scratch or through strategic refactors, with a strong focus on quality and long-term maintainability.',
          tech: ['Flutter', 'Android', 'iOS', 'Kotlin', 'Swift'],
        },
        {
          title: 'Architecture Modernization',
          description:
            'I help teams modernize client and service architectures with durable foundations, clear module boundaries, and patterns adapted to scale and team velocity.',
          tech: ['Clean Architecture', 'MVP', 'MVVM', 'MVI', 'KMP'],
        },
        {
          title: 'Backend & API Services',
          description:
            'I design and implement backend and API layers tailored to product use cases, reducing client complexity and improving reliability, security, and release speed.',
          tech: ['Ktor', 'Node.js', 'TypeScript', 'REST APIs'],
        },
        {
          title: 'CI/CD & Release Automation',
          description:
            'I set up and optimize delivery pipelines across mobile and backend stacks so teams can ship faster with confidence, repeatability, and strong release governance.',
          tech: ['GitHub Actions', 'Jenkins', 'Fastlane', 'Docker'],
        },
        {
          title: 'Quality, Stability & Security',
          description:
            'I improve engineering quality with robust test strategies, observability practices, and security-first implementation standards across critical user journeys.',
          tech: ['Unit Testing', 'Integration Testing', 'Firebase', 'Crash Analytics'],
        },
        {
          title: 'AI-Enabled Engineering & Automation',
          description:
            'I use AI pragmatically to accelerate engineering workflows, automate repetitive tasks, and improve delivery efficiency where it creates real value.',
          tech: [
            'AI-Assisted Delivery',
            'Workflow Automation',
            'Developer Tooling',
            'Productivity Systems',
          ],
        },
      ],
    },
    cv: {
      heading: 'CV',
      description: 'Download my latest CV in English.',
      title: 'English CV',
      subtitle: 'Senior Software Engineer',
      downloadCta: 'Download CV (EN)',
      file: {
        url: '/cv/Hedi_Hidouri_CV_EN.pdf',
        downloadName: 'Hedi_Hidouri_CV_EN.pdf',
      },
    },
    contact: {
      overline: 'Let\'s Build Something Solid',
      title: 'Available for freelance software engineering missions',
      description:
        'I partner with product teams to deliver robust software products, streamline mobile + backend architecture, and accelerate releases. If you need senior engineering support across Android, iOS, Flutter, backend, or AI-enabled delivery, let\'s talk.',
      cta: 'Start a Conversation',
    },
    footer: {
      builtBy: 'Built by Hedi Hidouri',
    },
  },

  fr: {
    head: {
      title: 'Hedi Hidouri | Ingénieur Logiciel Senior',
      description:
        'Ingénieur logiciel senior sur produits mobiles, services backend et delivery assisté par l IA.',
      locale: 'fr_FR',
      keywords: [
        'Ingénieur Logiciel Senior',
        'Ingenieur logiciel Paris',
        'Freelance ingenieur logiciel Paris',
        'Developpeur Flutter',
        'Developpeur Android',
        'Developpeur iOS',
        'Ingenieur backend',
        'Kotlin Multiplatform',
        'Ktor',
        'Node.js',
        'OpenAPI',
        'Python',
        'Docker',
        'Architecture mobile',
        'CI/CD mobile',
        'Engineering assiste par l IA',
      ],
    },
    layout: {
      skipToContent: 'Aller au contenu',
      whatsAppCta: 'WhatsApp',
      whatsAppAriaLabel: 'Ouvrir le chat WhatsApp',
    },
    nav: {
      homeAriaLabel: 'Accueil',
      menuButtonAriaLabel: 'Menu',
      languageSwitcherAriaLabel: 'Choisir la langue',
      links: [
        { name: 'A propos', url: '#about' },
        { name: 'Experience', url: '#jobs' },
        { name: 'Expertises', url: '#services' },
        { name: 'CV', url: '#cv' },
        { name: 'Contact', url: '#contact' },
      ],
    },
    hero: {
      intro: 'Bonjour, je suis',
      title: 'Hedi Hidouri.',
      subtitle: 'Ingénieur Logiciel Senior.',
      description:
        'Je conçois, développe et modernise des produits logiciels sur applications mobiles, services backend et workflows de delivery assistes par l IA. Mon objectif est simple: une execution fiable, une architecture durable et un delivery securise du MVP a l echelle.',
      quickStats: [
        { label: 'Localisation', value: 'Paris, France' },
        { label: 'Experience', value: '8+ ans' },
      ],
      cta: 'Travaillons Ensemble',
    },
    about: {
      heading: 'A propos',
      paragraphs: [
        'J accompagne des entreprises tech, fintech et produit dans la conception, le developpement et la modernisation de produits logiciels fiables, performants et penses pour durer.',
        'Sur des produits a forte volumetrie et a fortes contraintes (logistique, paiement, mobilite, banque), j aide les equipes a livrer des applications mobiles robustes, a fluidifier les interactions backend et a structurer une architecture mobile + backend maintenable.',
      ],
      valueTitle: 'Ma valeur ajoutee',
      valueList: [
        'Une vision end-to-end: mobile, backend, architecture, CI/CD.',
        'Une forte expertise sur les systèmes sensibles (sécurité, conformité, performance).',
        'Une capacité à cadrer un produit techniquement du MVP jusqu aux plateformes à grande échelle.',
        'Une approche pragmatique et orientée delivery: ce qui compte, c est ce qui sort en production.',
      ],
      techIntro: 'Technologies clés utilisées régulièrement:',
      skills: sharedSkills,
      snapshotTitle: 'Profil en bref',
      snapshotStats: [
        { label: 'Role', value: 'Ingénieur Logiciel Senior' },
        { label: 'Localisation', value: 'Paris, France (hybride / remote)' },
        { label: 'Langues', value: 'Francais, Anglais' },
        {
          label: 'Environnements preferes',
          value: 'Equipes produit, delivery Agile, plateformes logicielles a fort impact',
        },
      ],
      domains: [
        'Banque & services financiers',
        'E-commerce',
        'Grande distribution',
        'Plateformes digitales',
        'Services numeriques',
      ],
    },
    jobs: {
      heading: 'Experiences Professionnelles',
      tabsAriaLabel: 'Onglets experiences',
      items: [
        {
          title: 'Ingenieur Mobile Senior',
          company: 'INGroupe',
          location: 'Paris, France',
          range: 'avr. 2025 - Aujourd hui',
          url: 'https://ingroupe.com/',
          summary:
            'Intervention sur des cas d\'usage EUDI de bout en bout : apps et SDKs Android, iOS, Flutter, React Native et KMP, backends Ktor issuer/verifier, flux de vérification de proximité et environnements Docker/Docker Compose. Le périmètre couvre wallet, issuer, verifier, vérification d\'âge, badging digital, partage d\'identité et parcours liés au paiement.',
          points: [
            'Intervention sur l\'écosystème EUDI de bout en bout : applications wallet, services issuer, composants verifier, flux de proximité et intégrations SDK.',
            'Développement de composants Android, iOS, Flutter et Kotlin Multiplatform pour des cas d\'usage sécurisés d\'émission, de présentation et de vérification de credentials.',
            'Conception de services backend Ktor pour l\'orchestration issuer/verifier, les contrats API et des parcours connexes au paiement, avec des environnements locaux Dockerisés.',
            'Développement de SDKs et couches d\'intégration pour la vérification de proximité sur clients natifs et cross-platform, incluant QR, NFC/BLE et échanges mdoc.',
            'Mise en place de stacks Docker et Docker Compose pour exécuter des scénarios EUDI complets localement et accélérer les tests, démos et livraisons end-to-end.',
            'Delivery de cas d\'usage : vérification d\'âge, badging digital, partage d\'identité, vérification de proximité et parcours liés au paiement, avec garde-fous qualité CI/CD.',
          ],
        },
        {
          title: 'Ingenieur Mobile Senior',
          company: 'Stuart',
          location: 'Paris, France',
          range: 'nov. 2022 - avr. 2025',
          url: 'https://stuart.com/',
          points: [
            'Pilotage de la modernisation des parcours Android et iOS avec Flutter pour une plateforme logistique à grande échelle.',
            'Développement et supervision d\'un Backend For Frontend mobile en Node.js avec des endpoints adaptés aux besoins applicatifs.',
            'Orchestration de composants backend avec Docker et Kafka pour améliorer la fiabilité des échanges entre services et applications.',
            'Structuration et industrialisation de pipelines CI/CD via GitHub Actions et Fastlane pour automatiser builds et déploiements.',
            'Renforcement de la qualité logicielle grâce aux tests unitaires et d intégration dans un contexte Agile.',
            'Contribution à des domaines fonctionnels clés: optimisation de la livraison, suivi des courses et évolution des paiements.',
          ],
        },
        {
          title: 'Consultant en Developpement Mobile',
          company: 'Carrefour',
          location: 'Paris, France',
          range: 'mai 2021 - nov. 2022',
          url: 'https://www.carrefour.com/',
          points: [
            'Contribution à l évolution à grande échelle des applications Android Carrefour Drive et Livraison.',
            'Développement de nouvelles fonctionnalités et refontes UI/UX pour améliorer la fluidité, la performance et l expérience client.',
            'Modernisation de l architecture via MVI et Clean Architecture avec des stacks réactives (Flow, Coroutines).',
            'Mise en place de pratiques de modularisation et d injection de dépendances avec Hilt pour améliorer la maintenabilité.',
            'Optimisation de la CI mobile via Jenkins et Gradle Kotlin DSL (KTS).',
            'Renforcement de la fiabilité grâce aux tests unitaires et d intégration, en collaboration étroite avec les équipes produit et design.',
          ],
        },
        {
          title: 'Developpeur Mobile Android / iOS',
          company: 'JCDecaux',
          location: 'Paris, France',
          range: 'janv. 2021 - mai 2021',
          url: 'https://www.jcdecaux.com/',
          points: [
            'Développement et amélioration continue d applications mobiles de vélos en libre-service utilisées par plus d un million d utilisateurs.',
            'Implémentation de nouvelles fonctionnalités Android et iOS avec optimisation continue des performances globales.',
            'Réduction des incidents en production via des actions ciblées de stabilisation et de correction.',
            'Mise en place de tests unitaires et d intégration Android/iOS avec une couverture solide.',
            'Collaboration étroite avec les équipes produit pour clarifier les besoins métier et améliorer l expérience utilisateur.',
            'Delivery en cycles Agile/Scrum avec implication active sur la planification et l exécution.',
          ],
        },
        {
          title: 'Ingenieur Mobile iOS',
          company: 'HSBC',
          location: 'Paris, France',
          range: 'mars 2019 - dec. 2020',
          url: 'https://www.hsbc.com/',
          points: [
            'Amélioration continue de l application bancaire iOS avec un fort focus sur l expérience utilisateur et les fonctionnalités clés.',
            'Implémentation de fonctionnalités de sécurité avancées, incluant biométrie et provisioning wallet.',
            'Traduction des besoins métier et conformité en solutions techniques scalables.',
            'Réalisation de tests unitaires et d intégration, avec support des phases de recette interne.',
            'Investigation et correction d incidents en production pour améliorer la stabilité et la qualité de service.',
            'Contribution à l enrichissement des librairies internes, à la documentation technique et au partage de bonnes pratiques.',
          ],
        },
        {
          title: 'Ingenieur Developpeur Mobile',
          company: 'Bulldozer',
          location: 'Ariana, Tunisie',
          range: 'juil. 2017 - fev. 2019',
          url: 'https://www.linkedin.com/company/bulldozer/',
          points: [
            'Contribution au cycle de vie complet des applications mobiles: planification, développement, tests et déploiement sur stores.',
            'Réalisation d études techniques et de conceptions d architecture pour des produits clients multi-domaines.',
            'Développement de fonctionnalités Android et iOS avec une attention forte à la performance, à la qualité et à la maintenabilité.',
            'Mise en place de stratégies de tests unitaires, d intégration et UI pour renforcer la confiance en release.',
            'Participation aux migrations techniques et à la modernisation de bases applicatives existantes.',
            'Travail dans des environnements Agile (Scrum/Kanban) avec contribution active aux rituels et à l amélioration continue.',
          ],
        },
      ],
    },
    services: {
      heading: 'Expertises & Services',
      detailsLabel: 'Details',
      items: [
        {
          title: 'Delivery Produit Logiciel',
          description:
            'Je developpe des produits logiciels prets pour la production, sur applications mobiles et services associes, en creation from scratch ou via des refontes ciblees, avec un focus fort sur la qualite produit et la maintenabilite long terme.',
          tech: ['Flutter', 'Android', 'iOS', 'Kotlin', 'Swift'],
        },
        {
          title: 'Modernisation d Architecture',
          description:
            'J aide les equipes a moderniser leurs architectures client et service avec des fondations durables, des frontieres de modules claires et des patterns adaptes a l echelle et a la velocite equipe.',
          tech: ['Clean Architecture', 'MVP', 'MVVM', 'MVI', 'KMP'],
        },
        {
          title: 'Services Backend & API',
          description:
            'Je concois et implemente des couches backend et API orientees usages produit pour reduire la complexite cote client et ameliorer fiabilite, securite et vitesse de livraison.',
          tech: ['Ktor', 'Node.js', 'TypeScript', 'APIs REST'],
        },
        {
          title: 'CI/CD & Automatisation des Releases',
          description:
            'Je mets en place et optimise des pipelines de delivery sur mobile et backend pour livrer plus vite avec confiance, repetabilite et une gouvernance de release solide.',
          tech: ['GitHub Actions', 'Jenkins', 'Fastlane', 'Docker'],
        },
        {
          title: 'Qualite, Stabilite & Securite',
          description:
            'J ameliore la qualite engineering avec des strategies de test robustes, de bonnes pratiques d observabilite et des standards d implementation security-first sur les parcours critiques.',
          tech: ['Tests unitaires', 'Tests integration', 'Firebase', 'Crash Analytics'],
        },
        {
          title: 'Automatisation & Delivery assiste par l IA',
          description:
            'J utilise l IA de facon pragmatique pour accelerer les workflows d engineering, automatiser les taches repetitives et gagner en efficacite sur le delivery lorsque cela apporte une vraie valeur.',
          tech: [
            'Delivery assiste par l IA',
            'Automatisation de workflows',
            'Developer tooling',
            'Productivite engineering',
          ],
        },
      ],
    },
    cv: {
      heading: 'CV',
      description: 'Téléchargez mon CV à jour en français.',
      title: 'CV Français',
      subtitle: 'Ingénieur Software Senior',
      downloadCta: 'Télécharger le CV (FR)',
      file: {
        url: '/cv/Hedi_Hidouri_CV_FR.pdf',
        downloadName: 'Hedi_Hidouri_CV_FR.pdf',
      },
    },
    contact: {
      overline: 'Construisons quelque chose de solide',
      title: 'Disponible pour des missions freelance en software engineering',
      description:
        'J accompagne des equipes produit pour livrer des produits logiciels robustes, clarifier l architecture mobile + backend et accelerer les releases. Si vous avez besoin d un support senior sur Android, iOS, Flutter, backend ou le delivery assiste par l IA, echangeons.',
      cta: 'Démarrer une discussion',
    },
    footer: {
      builtBy: 'Concu par Hedi Hidouri',
    },
  },
};
